import Switch from '@/components/UI/FormElements/Switch';
import AsyncSelect from 'react-select/async';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import styles from './DurationPicker.module.scss';
import { _isEmptyString, _notNil } from '@/littledash';
import {
  allDayToOption,
  type DurationPickerState,
  fromOptionData,
  initialState,
  minutesInDay,
  stateToDurationValue,
  toggleAllDayTask,
  toOptionData,
} from '@/components/UI/DurationPicker/DurationPicker.util';
import { type TaskTimeDuration } from '@/model/Task.model';

type DurationPickerProps = {
  value: TaskTimeDuration;
  disabled?: boolean;
  onChange: (value: TaskTimeDuration) => void;
  onValidationChange?: (isValid: boolean) => void;
};
export const DurationPicker: FC<DurationPickerProps> = ({ value, disabled = false, onChange, onValidationChange }) => {
  const [state, updateState] = useState<DurationPickerState>(() => initialState(value));
  const { fromOptionsMap, fromOptions, fromOptionsFuse } = useMemo(fromOptionData, []);
  const { toOptionsMap, toOptions, toOptionsFuse } = useMemo(
    () => toOptionData(state.from, state.isCrossMidnight ? state.duration : undefined),
    [state.from, state.isCrossMidnight, state.duration]
  );
  useEffect(() => {
    const newState = initialState(value);
    updateState((prevState) => {
      if (
        prevState.allDay === newState.allDay &&
        prevState.from === newState.from &&
        prevState.duration === newState.duration &&
        prevState.isCrossMidnight === newState.isCrossMidnight
      ) {
        return prevState;
      }

      // If initialState auto-converted to all-day, notify parent
      if (value.type === 'timed' && newState.allDay) {
        onChange(stateToDurationValue(newState));
      }

      return newState;
    });
  }, [updateState, value, onChange]);

  // Notify parent of validation state
  useEffect(() => {
    onValidationChange?.(!state.isCrossMidnight);
  }, [state.isCrossMidnight, onValidationChange]);
  const handleStateChange = (update: (prev: DurationPickerState) => DurationPickerState) => {
    if (!disabled) {
      updateState((prev) => {
        let result = update(prev);

        // Auto-toggle to all-day if duration is exactly 24 hours (1440 minutes)
        if (!result.allDay && result.duration === minutesInDay && !result.isCrossMidnight) {
          result = { ...result, allDay: true, from: -1 };
        }

        onChange(stateToDurationValue(result));
        return result;
      });
    }
  };
  const loadFromOptions = useCallback(
    (inputValue: string) => {
      return Promise.resolve(fromOptionsFuse.search(inputValue).map((result) => result.item));
    },
    [fromOptions, fromOptionsFuse]
  );
  const loadToOptions = useCallback(
    (inputValue: string) => {
      return Promise.resolve(
        _isEmptyString(inputValue) ? toOptions : toOptionsFuse.search(inputValue).map((result) => result.item)
      );
    },
    [toOptions, toOptionsFuse]
  );
  return (
    <div
      className="flex flex-row items-center justify-start"
      data-test-component="DurationPicker"
      data-test-element="container"
      data-test-is-valid={!state.isCrossMidnight}
    >
      <span data-test-element="from-select-container" className="ui__select">
        <AsyncSelect
          classNamePrefix="ui__select"
          className={`${styles.DurationSelect} ${state.isCrossMidnight ? 'duration-picker__error' : ''}`}
          defaultOptions={fromOptions}
          loadOptions={loadFromOptions}
          isDisabled={state.allDay || disabled}
          value={state.allDay ? fromOptionsMap.get(0) : fromOptionsMap.get(state.from)}
          isOptionSelected={(option) => option.offset === state.from}
          isOptionDisabled={(option) => option.offset + state.duration > minutesInDay}
          onChange={(value) => {
            if (_notNil(value)) {
              handleStateChange((prev) => ({ ...prev, from: value.offset, isCrossMidnight: false }));
            }
          }}
        />
      </span>

      <div className="mh2">to</div>
      <span className="mr2 ui__select" data-test-element="to-select-container">
        <AsyncSelect
          classNamePrefix="ui__select"
          className={`${styles.DurationSelect} ${state.isCrossMidnight ? 'duration-picker__error' : ''}`}
          defaultOptions={toOptions}
          loadOptions={loadToOptions}
          isDisabled={state.allDay || disabled}
          value={state.allDay ? allDayToOption : toOptionsMap.get(state.duration)}
          isOptionSelected={(option) => option.duration === state.duration}
          isOptionDisabled={(option) => Boolean(option.isInvalid)}
          onChange={(value) => {
            if (_notNil(value) && !value.isInvalid) {
              handleStateChange((prev) => ({ ...prev, duration: value.duration, isCrossMidnight: false }));
            }
          }}
        />
      </span>
      <Switch
        value={state.allDay}
        disabled={disabled}
        className="mr2"
        onChange={() => handleStateChange(toggleAllDayTask)}
      />
      <span>All day</span>
    </div>
  );
};
